// src/curated/curatedTripsData.js
// RouteChef curated trips: a set itinerary the traveller can edit (by chat or by hand).
//
// Same day/block shape as creator trips, so creatorTripEngine.buildInstance() prices
// getting there / back and stays without changes. Extra fields used only by curated pages:
//   block.id              stable id, edits and hotel picks key off it
//   EXPERIENCE.approx_pp  rough ticket cost, shown as a hint only. Activities are NOT booked or
//                         charged by RouteChef: travellers pay at the venue, so they stay out of totals.
//   default_origin        start cities to try, in order, until one is in ORIGIN_CITIES
//   hotel_search_place    place_name sent to the /hotels API for each stay city
//   exit.leave_after      earliest departure on the last day (the engine needs it)
//   search_modes          modes for getting there / back (default Train + Flight + Bus)
//   STAY.tier             budget | comfort | premium (what the curator picked)
//   hotel_options[city]   curator shortlist shown in the hotel picker under the live results
//   activity_pool         things the agent can add ("add rafting", "something for kids")
//
// Shortlist hotel prices are estimates; those hotels are confirmed by RouteChef ops after the
// lead comes in. Live hotels from the /hotels API are bookable instantly.

import { ORIGIN_CITIES } from "../creator/creatorTripsData";

export { ORIGIN_CITIES };
const ORIGIN_CITIES_LIST = () => ORIGIN_CITIES || [];

export const TIERS = ["budget", "comfort", "premium"];
export const TIER_LABEL = { budget: "Budget", comfort: "Comfort", premium: "Premium" };

export const CURATED_TRIPS = [
    // ───────────────────────────────────────────────────────────── Coorg
    {
        slug: "coorg-coffee-and-clouds",
        default_origin: ["Bengaluru", "Bangalore", "Mysuru", "Mysore"],
        hotel_search_place: { Madikeri: "Madikeri, Coorg" },
        title: "Coorg: Coffee and Clouds",
        subtitle: "3 days · Madikeri, Dubare, Bylakuppe",
        curator: "RouteChef Trips",
        cover: "",
        cover_gradient: "linear-gradient(135deg, #14532D 0%, #166534 45%, #A16207 100%)",
        vibe: ["Hills", "Coffee estates", "Easy pace"],
        best_months: "Oct to Mar",
        max_group: 8,
        creator_markup_pp: 1200,
        reel_url: null,
        entry: { city: "Madikeri", name: "Madikeri town", lat: 12.4244, lng: 75.7382, start_by: "11:00" },
        exit: { city: "Madikeri", name: "Madikeri town", lat: 12.4244, lng: 75.7382, day_offset: 2, leave_after: "14:00" },
        days: [
            {
                day: 1, title: "Arrive and settle in",
                blocks: [
                    { id: "co-stay-1", type: "STAY", city: "Madikeri", nights: 2, preferred: "Coorg Wilderness Resort & Spa", tier: "comfort", lock: "PREFERRED", fallback_pn: 7500 },
                    { id: "co-abbey", type: "STOP", name: "Abbey Falls", time: "15:00", note: "Short walk through coffee and pepper", lock: "FLEXIBLE" },
                    { id: "co-raja", type: "STOP", name: "Raja's Seat sunset", time: "17:30", note: "Best view in town", lock: "LOCKED" },
                    { id: "co-tip-1", type: "TIP", text: "Carry a light jacket, evenings get cool from November." },
                ],
            },
            {
                day: 2, title: "Elephants and the Golden Temple",
                blocks: [
                    { id: "co-dubare", type: "EXPERIENCE", name: "Dubare elephant camp", time: "08:30", note: "Reach early, bathing session ends by 10:30", lock: "PREFERRED", approx_pp: 300 },
                    { id: "co-namdroling", type: "STOP", name: "Namdroling Monastery, Bylakuppe", time: "12:30", note: "Lunch at the Tibetan settlement", lock: "FLEXIBLE" },
                    { id: "co-harangi", type: "STOP", name: "Harangi backwaters", time: "16:00", lock: "FLEXIBLE" },
                ],
            },
            {
                day: 3, title: "Estate morning, head home",
                blocks: [
                    { id: "co-estate", type: "EXPERIENCE", name: "Coffee estate walk and tasting", time: "08:00", note: "Bean to cup with a planter", lock: "LOCKED", approx_pp: 600 },
                    { id: "co-market", type: "STOP", name: "Madikeri market for coffee and spices", time: "11:00", lock: "FLEXIBLE" },
                ],
            },
        ],
        activity_pool: [
            { id: "co-p-mandal", type: "EXPERIENCE", name: "Mandalpatti jeep ride", time: "06:00", note: "Sunrise above the clouds", approx_pp: 900, tags: ["sunrise", "adventure", "jeep", "view"] },
            { id: "co-p-rafting", type: "EXPERIENCE", name: "Barapole river rafting", time: "10:00", note: "Grade 3 rapids, Jun to Sep only", approx_pp: 1800, tags: ["rafting", "adventure", "water"] },
            { id: "co-p-talacauvery", type: "STOP", name: "Talacauvery and Bhagamandala", time: "09:00", note: "Source of the Kaveri", tags: ["temple", "spiritual", "view"] },
            { id: "co-p-fort", type: "STOP", name: "Madikeri Fort", time: "11:00", tags: ["history", "heritage"] },
            { id: "co-p-nisargadhama", type: "STOP", name: "Nisargadhama island", time: "15:00", note: "Bamboo groves, easy for kids", tags: ["kids", "nature", "easy"] },
            { id: "co-p-spa", type: "EXPERIENCE", name: "Ayurvedic massage", time: "17:00", approx_pp: 2200, tags: ["spa", "relax", "massage"] },
            { id: "co-p-pandi", type: "EXPERIENCE", name: "Kodava pandi curry dinner", time: "20:00", note: "Local home kitchen", approx_pp: 700, tags: ["food", "dinner", "local"] },
        ],
        hotel_options: {
            Madikeri: [
                { code: "CO-ZOSTEL", name: "Zostel Coorg", tier: "budget", price_pn: 2200, rating: 4.3, area: "Madikeri", meal: "Room only", perks: ["Private rooms", "Cafe"] },
                { code: "CO-HOMESTAY", name: "Estate homestay (RouteChef verified)", tier: "budget", price_pn: 3200, rating: 4.5, area: "Near Abbey Falls", meal: "Breakfast", perks: ["Home food", "Estate walk"] },
                { code: "CO-CLUBM", name: "Club Mahindra Madikeri", tier: "comfort", price_pn: 6800, rating: 4.2, area: "Galibeedu", meal: "Breakfast", perks: ["Pool", "Kids club"] },
                { code: "CO-WILD", name: "Coorg Wilderness Resort & Spa", tier: "comfort", price_pn: 7500, rating: 4.4, area: "Madikeri", meal: "Breakfast", perks: ["Valley views", "Spa"] },
                { code: "CO-TAJ", name: "Taj Madikeri Resort & Spa", tier: "premium", price_pn: 19000, rating: 4.7, area: "Galibeedu", meal: "Breakfast", perks: ["Rainforest setting", "Infinity pool"] },
                { code: "CO-TAMARA", name: "The Tamara Coorg", tier: "premium", price_pn: 24000, rating: 4.8, area: "Kabbinakad", meal: "Breakfast", perks: ["Estate cottages", "Coffee trail"] },
            ],
        },
    },

    // ───────────────────────────────────────────────────────────── Rishikesh
    {
        slug: "rishikesh-river-and-ritual",
        default_origin: ["Delhi", "New Delhi"],
        hotel_search_place: { Rishikesh: "Tapovan, Rishikesh" },
        title: "Rishikesh: River and Ritual",
        subtitle: "3 days · Tapovan, Laxman Jhula, Kunjapuri",
        curator: "RouteChef Trips",
        cover: "",
        cover_gradient: "linear-gradient(135deg, #0C4A6E 0%, #0E7490 50%, #F59E0B 100%)",
        vibe: ["Rafting", "Ganga aarti", "Cafes"],
        best_months: "Sep to Jun",
        max_group: 10,
        creator_markup_pp: 1000,
        reel_url: null,
        entry: { city: "Rishikesh", name: "Rishikesh", lat: 30.0869, lng: 78.2676, start_by: "12:00" },
        exit: { city: "Rishikesh", name: "Rishikesh", lat: 30.0869, lng: 78.2676, day_offset: 2, leave_after: "13:00" },
        days: [
            {
                day: 1, title: "Ghats and the evening aarti",
                blocks: [
                    { id: "ri-stay-1", type: "STAY", city: "Rishikesh", nights: 2, preferred: "Aloha on the Ganges", tier: "comfort", lock: "PREFERRED", fallback_pn: 6500 },
                    { id: "ri-beatles", type: "STOP", name: "Beatles Ashram", time: "14:30", note: "Graffiti halls and meditation domes", lock: "FLEXIBLE" },
                    { id: "ri-aarti", type: "EXPERIENCE", name: "Ganga aarti at Parmarth Niketan", time: "18:00", note: "Get there 30 min early for a step seat", lock: "LOCKED", approx_pp: 0 },
                ],
            },
            {
                day: 2, title: "Rapids and cafes",
                blocks: [
                    { id: "ri-raft", type: "EXPERIENCE", name: "Shivpuri to NIM beach rafting (16 km)", time: "09:00", note: "Grade 3, with cliff jump", lock: "PREFERRED", approx_pp: 1200 },
                    { id: "ri-cafe", type: "STOP", name: "Lunch at a Tapovan cafe", time: "13:30", lock: "FLEXIBLE" },
                    { id: "ri-laxman", type: "STOP", name: "Laxman Jhula and Ram Jhula walk", time: "16:30", lock: "FLEXIBLE" },
                    { id: "ri-tip-1", type: "TIP", text: "Rafting pauses during peak monsoon, usually July and August." },
                ],
            },
            {
                day: 3, title: "Sunrise temple, then home",
                blocks: [
                    { id: "ri-kunja", type: "STOP", name: "Kunjapuri sunrise", time: "05:00", note: "Himalayan peaks on a clear day", lock: "LOCKED" },
                    { id: "ri-neer", type: "STOP", name: "Neer Garh waterfall", time: "10:30", lock: "FLEXIBLE" },
                ],
            },
        ],
        activity_pool: [
            { id: "ri-p-bungee", type: "EXPERIENCE", name: "Bungee at Mohan Chatti", time: "10:00", note: "83 m jump", approx_pp: 3700, tags: ["bungee", "adventure", "jump"] },
            { id: "ri-p-yoga", type: "EXPERIENCE", name: "Morning yoga class", time: "07:00", approx_pp: 500, tags: ["yoga", "relax", "wellness", "morning"] },
            { id: "ri-p-triveni", type: "EXPERIENCE", name: "Triveni Ghat aarti", time: "18:00", approx_pp: 0, tags: ["aarti", "spiritual", "evening"] },
            { id: "ri-p-camp", type: "EXPERIENCE", name: "Riverside camp evening and bonfire", time: "19:00", approx_pp: 1500, tags: ["camp", "bonfire", "night"] },
            { id: "ri-p-haridwar", type: "STOP", name: "Har Ki Pauri, Haridwar", time: "16:00", note: "45 min drive", tags: ["haridwar", "spiritual", "aarti"] },
            { id: "ri-p-sound", type: "EXPERIENCE", name: "Sound healing session", time: "17:00", approx_pp: 800, tags: ["wellness", "relax", "meditation"] },
            { id: "ri-p-kayak", type: "EXPERIENCE", name: "Kayaking lesson", time: "09:00", approx_pp: 2000, tags: ["kayak", "water", "adventure"] },
        ],
        hotel_options: {
            Rishikesh: [
                { code: "RI-ZOSTEL", name: "Zostel Rishikesh", tier: "budget", price_pn: 1800, rating: 4.4, area: "Tapovan", meal: "Room only", perks: ["River view deck", "Cafe"] },
                { code: "RI-KINARE", name: "Ganga Kinare", tier: "comfort", price_pn: 7000, rating: 4.3, area: "Virbhadra Rd", meal: "Breakfast", perks: ["Private ghat", "Yoga"] },
                { code: "RI-DIVINE", name: "Divine Resort", tier: "comfort", price_pn: 5200, rating: 4.1, area: "Laxman Jhula", meal: "Breakfast", perks: ["Walk to jhula"] },
                { code: "RI-ALOHA", name: "Aloha on the Ganges", tier: "comfort", price_pn: 6500, rating: 4.4, area: "Tapovan", meal: "Breakfast", perks: ["Riverfront", "Pool"] },
                { code: "RI-TAJ", name: "Taj Rishikesh Resort & Spa", tier: "premium", price_pn: 26000, rating: 4.8, area: "Singthali", meal: "Breakfast", perks: ["Valley villas", "Spa"] },
            ],
        },
    },

    // ───────────────────────────────────────────────────────────── Munnar + Alleppey
    {
        slug: "munnar-to-alleppey",
        default_origin: ["Kochi", "Cochin", "Bengaluru", "Bangalore", "Chennai"],
        hotel_search_place: { Munnar: "Munnar, Kerala", Alleppey: "Alappuzha, Kerala" },
        title: "Munnar to Alleppey: Tea and Backwaters",
        subtitle: "4 days · Munnar, Top Station, a night on a houseboat",
        curator: "RouteChef Trips",
        cover: "",
        cover_gradient: "linear-gradient(135deg, #065F46 0%, #059669 45%, #0369A1 100%)",
        vibe: ["Tea hills", "Houseboat", "Couples"],
        best_months: "Sep to Mar",
        max_group: 6,
        creator_markup_pp: 1800,
        reel_url: null,
        entry: { city: "Munnar", name: "Munnar town", lat: 10.0889, lng: 77.0595, start_by: "13:00" },
        exit: { city: "Alleppey", name: "Alleppey", lat: 9.4981, lng: 76.3388, day_offset: 3, leave_after: "13:00" },
        days: [
            {
                day: 1, title: "Into the tea country",
                blocks: [
                    { id: "mu-stay-1", type: "STAY", city: "Munnar", nights: 2, preferred: "Fragrant Nature Munnar", tier: "comfort", lock: "PREFERRED", fallback_pn: 8000 },
                    { id: "mu-museum", type: "EXPERIENCE", name: "KDHP Tea Museum", time: "15:00", note: "Short film and factory floor", lock: "FLEXIBLE", approx_pp: 200 },
                    { id: "mu-kathakali", type: "EXPERIENCE", name: "Kathakali show", time: "18:00", lock: "FLEXIBLE", approx_pp: 400 },
                ],
            },
            {
                day: 2, title: "Peaks and lakes",
                blocks: [
                    { id: "mu-eravikulam", type: "EXPERIENCE", name: "Eravikulam National Park", time: "08:00", note: "Nilgiri tahr spotting, book the first bus", lock: "LOCKED", approx_pp: 200 },
                    { id: "mu-mattupetty", type: "STOP", name: "Mattupetty dam and Echo Point", time: "12:30", lock: "FLEXIBLE" },
                    { id: "mu-top", type: "STOP", name: "Top Station viewpoint", time: "15:00", lock: "FLEXIBLE" },
                    { id: "mu-tip-1", type: "TIP", text: "Eravikulam usually closes in Feb and Mar for the tahr calving season." },
                ],
            },
            {
                day: 3, title: "Down to the backwaters",
                blocks: [
                    { id: "mu-move", type: "MOVE", mode: "cab", from: "Munnar", to: "Alleppey", label: "Private cab", depart: "08:00", duration: "4h 30m", price_pp: 2200, lock: "PREFERRED" },
                    { id: "mu-stay-2", type: "STAY", city: "Alleppey", nights: 1, preferred: "Deluxe AC houseboat", tier: "comfort", lock: "PREFERRED", fallback_pn: 11000 },
                    { id: "mu-cruise", type: "EXPERIENCE", name: "Houseboat cruise on Vembanad", time: "12:30", note: "Lunch and dinner on board", lock: "LOCKED", approx_pp: 0 },
                ],
            },
            {
                day: 4, title: "Beach morning, head home",
                blocks: [
                    { id: "mu-marari", type: "STOP", name: "Marari beach", time: "09:30", lock: "FLEXIBLE" },
                ],
            },
        ],
        activity_pool: [
            { id: "mu-p-trek", type: "EXPERIENCE", name: "Lakshmi hills tea trail trek", time: "07:00", approx_pp: 900, tags: ["trek", "hike", "adventure", "morning"] },
            { id: "mu-p-spice", type: "EXPERIENCE", name: "Spice plantation tour", time: "10:00", approx_pp: 300, tags: ["spice", "plantation", "kids", "easy"] },
            { id: "mu-p-kolukkumalai", type: "EXPERIENCE", name: "Kolukkumalai sunrise jeep", time: "04:30", note: "Highest tea estate", approx_pp: 1800, tags: ["sunrise", "jeep", "view", "adventure"] },
            { id: "mu-p-canoe", type: "EXPERIENCE", name: "Village canoe ride, Alleppey", time: "07:00", approx_pp: 600, tags: ["canoe", "village", "water", "morning"] },
            { id: "mu-p-ayur", type: "EXPERIENCE", name: "Ayurvedic massage", time: "17:00", approx_pp: 2500, tags: ["spa", "massage", "relax"] },
            { id: "mu-p-elephant", type: "EXPERIENCE", name: "Elephant junction, Chinnakanal", time: "11:00", approx_pp: 700, tags: ["elephant", "kids"] },
        ],
        hotel_options: {
            Munnar: [
                { code: "MU-HOMESTAY", name: "Tea estate homestay (RouteChef verified)", tier: "budget", price_pn: 2800, rating: 4.4, area: "Pallivasal", meal: "Breakfast", perks: ["Home food", "Estate views"] },
                { code: "MU-TEACOUNTY", name: "Tea County, KTDC", tier: "budget", price_pn: 4200, rating: 4.0, area: "Munnar town", meal: "Breakfast", perks: ["Central"] },
                { code: "MU-WINDY", name: "Chandys Windy Woods", tier: "comfort", price_pn: 9500, rating: 4.5, area: "Devikulam Rd", meal: "Breakfast", perks: ["Valley views", "Pool"] },
                { code: "MU-FRAGRANT", name: "Fragrant Nature Munnar", tier: "comfort", price_pn: 8000, rating: 4.5, area: "Pothamedu", meal: "Breakfast", perks: ["Balcony rooms", "Spa"] },
                { code: "MU-BLANKET", name: "Blanket Hotel & Spa", tier: "premium", price_pn: 16000, rating: 4.7, area: "Attukad", meal: "Breakfast", perks: ["Waterfall view", "Infinity pool"] },
            ],
            Alleppey: [
                { code: "AL-HB-STD", name: "Standard houseboat (1 bedroom)", tier: "budget", price_pn: 7500, rating: 4.1, area: "Punnamada", meal: "All meals", perks: ["AC at night"] },
                { code: "AL-HB-DLX", name: "Deluxe AC houseboat", tier: "comfort", price_pn: 11000, rating: 4.4, area: "Punnamada", meal: "All meals", perks: ["Full-day AC", "Sundeck"] },
                { code: "AL-PUNNAMADA", name: "Punnamada Resort", tier: "comfort", price_pn: 9000, rating: 4.3, area: "Punnamada lake", meal: "Breakfast", perks: ["Lake cottages"] },
                { code: "AL-HB-PREM", name: "Premium glass-roof houseboat", tier: "premium", price_pn: 19000, rating: 4.6, area: "Punnamada", meal: "All meals", perks: ["Private chef", "Upper deck"] },
                { code: "AL-LAKEPALACE", name: "Lake Palace Resort", tier: "premium", price_pn: 15000, rating: 4.5, area: "Thirumala ward", meal: "Breakfast", perks: ["Lake villas", "Pool"] },
            ],
        },
    },

    // ───────────────────────────────────────────────────────────── Manali
    {
        slug: "manali-snow-and-old-town",
        default_origin: ["Delhi", "New Delhi", "Chandigarh"],
        search_modes: ["Flight", "Bus"],   // no railhead in Manali: overnight bus, or fly to Kullu
        hotel_search_place: { Manali: "Manali, Himachal Pradesh" },
        title: "Manali: Snow Points and Old Town",
        subtitle: "4 days · Old Manali, Solang, Sissu",
        curator: "RouteChef Trips",
        cover: "",
        cover_gradient: "linear-gradient(135deg, #1E3A8A 0%, #475569 50%, #E2E8F0 100%)",
        vibe: ["Mountains", "Snow", "Cafes"],
        best_months: "Oct to Jun",
        max_group: 10,
        creator_markup_pp: 1300,
        reel_url: null,
        entry: { city: "Manali", name: "Manali bus stand", lat: 32.2432, lng: 77.1892, start_by: "10:00" },
        exit: { city: "Manali", name: "Manali bus stand", lat: 32.2432, lng: 77.1892, day_offset: 3, leave_after: "14:00" },
        days: [
            {
                day: 1, title: "Settle in, Old Manali evening",
                blocks: [
                    { id: "ma-stay-1", type: "STAY", city: "Manali", nights: 3, preferred: "Johnson Lodge", tier: "comfort", lock: "PREFERRED", fallback_pn: 5500 },
                    { id: "ma-hadimba", type: "STOP", name: "Hadimba Devi Temple", time: "14:30", note: "Cedar forest walk around it", lock: "FLEXIBLE" },
                    { id: "ma-old", type: "STOP", name: "Old Manali cafes and the Manu Temple", time: "17:00", lock: "LOCKED" },
                    { id: "ma-tip-1", type: "TIP", text: "Most overnight buses reach early morning. Check-in is usually 12:00, ask us for early check-in." },
                ],
            },
            {
                day: 2, title: "Solang valley",
                blocks: [
                    { id: "ma-solang", type: "STOP", name: "Solang Valley", time: "09:00", note: "Snow from Dec to Mar", lock: "LOCKED" },
                    { id: "ma-ropeway", type: "EXPERIENCE", name: "Solang ropeway", time: "11:00", note: "Weather permitting", lock: "FLEXIBLE", approx_pp: 800 },
                    { id: "ma-vashisht", type: "STOP", name: "Vashisht hot springs", time: "16:30", lock: "FLEXIBLE" },
                ],
            },
            {
                day: 3, title: "Through the Atal Tunnel",
                blocks: [
                    { id: "ma-sissu", type: "STOP", name: "Atal Tunnel and Sissu", time: "08:30", note: "Local taxi, permit included in the fare", lock: "PREFERRED" },
                    { id: "ma-mall", type: "STOP", name: "Mall Road evening", time: "18:00", lock: "FLEXIBLE" },
                    { id: "ma-tip-2", type: "TIP", text: "Rohtang needs a separate permit and closes in winter. Ask our team if you want it." },
                ],
            },
            {
                day: 4, title: "Waterfall walk, head home",
                blocks: [
                    { id: "ma-jogini", type: "STOP", name: "Jogini Falls trail", time: "08:00", note: "About 3 km, easy to moderate", lock: "FLEXIBLE" },
                ],
            },
        ],
        activity_pool: [
            { id: "ma-p-paragliding", type: "EXPERIENCE", name: "Paragliding at Solang", time: "10:00", approx_pp: 3000, tags: ["paragliding", "adventure", "fly"] },
            { id: "ma-p-ski", type: "EXPERIENCE", name: "Beginner skiing lesson", time: "10:00", note: "Dec to Feb", approx_pp: 1500, tags: ["ski", "snow", "adventure"] },
            { id: "ma-p-naggar", type: "STOP", name: "Naggar Castle and Roerich Gallery", time: "11:00", tags: ["history", "art", "heritage"] },
            { id: "ma-p-rafting", type: "EXPERIENCE", name: "Beas river rafting", time: "10:00", note: "Apr to Jun", approx_pp: 1000, tags: ["rafting", "water", "adventure"] },
            { id: "ma-p-zorb", type: "EXPERIENCE", name: "Zorbing at Solang", time: "12:00", approx_pp: 600, tags: ["kids", "fun", "adventure"] },
            { id: "ma-p-trout", type: "EXPERIENCE", name: "Trout dinner at a riverside cafe", time: "20:00", approx_pp: 700, tags: ["food", "dinner", "local"] },
            { id: "ma-p-monastery", type: "STOP", name: "Himalayan Nyingmapa Buddhist Monastery", time: "15:00", tags: ["monastery", "spiritual", "easy"] },
        ],
        hotel_options: {
            Manali: [
                { code: "MA-ZOSTEL", name: "Zostel Manali", tier: "budget", price_pn: 1800, rating: 4.3, area: "Old Manali", meal: "Room only", perks: ["Cafe", "Valley view"] },
                { code: "MA-HOMESTAY", name: "Apple orchard homestay (RouteChef verified)", tier: "budget", price_pn: 2600, rating: 4.5, area: "Old Manali", meal: "Breakfast", perks: ["Home food", "Heater"] },
                { code: "MA-JOHNSON", name: "Johnson Lodge", tier: "comfort", price_pn: 5500, rating: 4.3, area: "Circuit House Rd", meal: "Breakfast", perks: ["Walk to Mall Road", "Garden"] },
                { code: "MA-SNOWVALLEY", name: "Snow Valley Resorts", tier: "comfort", price_pn: 6200, rating: 4.2, area: "Log Huts Area", meal: "Breakfast", perks: ["Mountain view", "Restaurant"] },
                { code: "MA-HIMALAYAN", name: "The Himalayan", tier: "premium", price_pn: 14000, rating: 4.6, area: "Hadimba Rd", meal: "Breakfast", perks: ["Castle-style rooms", "Spa"] },
                { code: "MA-SPAN", name: "Span Resort & Spa", tier: "premium", price_pn: 16000, rating: 4.6, area: "Kullu-Manali Hwy", meal: "Breakfast", perks: ["Riverside", "Spa"] },
            ],
        },
    },
];

export const DEFAULT_CURATED_SEARCH_MODES = ["Train", "Flight", "Bus"];

export const getCuratedTrip = (slug) => CURATED_TRIPS.find((t) => t.slug === slug) || null;

const normCity = (s) => String(s || "").toLowerCase().replace(/[^a-z]/g, "");

/** First of the trip's default_origin cities that we can plan from, else the first origin city. */
export const defaultOriginFor = (trip) => {
    for (const want of trip?.default_origin || []) {
        const hit = ORIGIN_CITIES_LIST().find((c) => normCity(c.name) === normCity(want));
        if (hit) return hit;
    }
    return ORIGIN_CITIES_LIST()[0] || null;
};
